// Cross-feature import through the schools feature's PUBLIC surface
// (its index.ts), which is the only sanctioned path per
// adding-a-react-feature.md §8 — schools/hooks.ts already imports
// institutionKeys the same way. Needed because an institution's crest
// is what every non-overriding School displays.
import { schoolKeys } from "@features/schools"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { institutionsApi, type UpdateInstitutionPayload } from "./api"
import { institutionKeys } from "./queryKeys"

import type { EvaluationCalendar, EvaluationPeriodIn } from "@/types"

export const useInstitutions = (trashOnly = false) =>
  useQuery({
    queryKey: institutionKeys.list(trashOnly),
    queryFn: () => institutionsApi.list(trashOnly),
    // Never across the trash boundary (see professors/hooks.ts).
    placeholderData: (prev, prevQuery) =>
      (prevQuery?.queryKey[2] as { trashOnly?: boolean } | undefined)?.trashOnly === trashOnly
        ? prev
        : undefined,
  })

export const useInstitution = (id: string | undefined) =>
  useQuery({
    queryKey: institutionKeys.detail(id ?? ""),
    queryFn: () => institutionsApi.get(id as string),
    enabled: !!id,
  })

export const useInstitutionHealth = (id: string | undefined) =>
  useQuery({
    queryKey: institutionKeys.health(id ?? ""),
    queryFn: () => institutionsApi.health(id as string),
    enabled: !!id,
  })

// PRD-61 FR-1.5. The previous year's payload stays up while another year
// loads, so the year select keeps its options; the card shows a skeleton
// while `isPlaceholderData`.
export const useInstitutionCalendar = (id: string | undefined, schoolYear: string | null) =>
  useQuery({
    queryKey: institutionKeys.calendar(id ?? "", schoolYear),
    queryFn: () => institutionsApi.calendar(id as string, schoolYear),
    enabled: !!id,
    placeholderData: (prev) => prev,
  })

export const useSaveInstitutionCalendar = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      schoolYear,
      periods,
    }: {
      id: string
      schoolYear: string
      periods: EvaluationPeriodIn[]
    }) => institutionsApi.saveCalendar(id, schoolYear, periods),
    onSuccess: (saved, { id }) => {
      // The PUT answers with the calendar. Every cached copy of that year —
      // asked for by name or as "the year in course" — takes it.
      qc.setQueriesData<EvaluationCalendar>(
        { queryKey: institutionKeys.calendarsFor(id) },
        (old) => (old?.school_year === saved.school_year ? saved : old),
      )
      // The health line, and every school that inherits this calendar.
      void qc.invalidateQueries({ queryKey: institutionKeys.health(id) })
      void qc.invalidateQueries({ queryKey: schoolKeys.calendars() })
    },
  })
}

export const useRecomputeInstitutionSnapshot = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => institutionsApi.recomputeSnapshot(id),
    onSuccess: (_data, id) => qc.invalidateQueries({ queryKey: institutionKeys.health(id) }),
  })
}

export const useCreateInstitution = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: institutionsApi.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: institutionKeys.all }),
  })
}

// PRD-55 — the institution's crest is the default every School inherits,
// so changing it changes what schools with no override display. Both
// lists are invalidated.
export const useUploadInstitutionLogo = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => institutionsApi.uploadLogo(id, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: institutionKeys.all })
      void qc.invalidateQueries({ queryKey: schoolKeys.all })
    },
  })
}

export const useRemoveInstitutionLogo = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => institutionsApi.removeLogo(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: institutionKeys.all })
      void qc.invalidateQueries({ queryKey: schoolKeys.all })
    },
  })
}

export const useUpdateInstitution = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInstitutionPayload }) =>
      institutionsApi.update(id, data),
    // A school that inherits the scale reads it through its own detail.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: institutionKeys.all })
      void qc.invalidateQueries({ queryKey: schoolKeys.all })
    },
  })
}

export const useDeleteInstitution = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => institutionsApi.remove(id),
    // A cascade soft-delete touches schools/professors/admins too — drop the
    // whole admin cache so every dependent list refetches.
    onSuccess: () => qc.invalidateQueries(),
  })
}

export const useRestoreInstitution = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => institutionsApi.restore(id),
    onSuccess: () => qc.invalidateQueries(),
  })
}
